import { useMemo, useState } from 'react';
import { AuditEntry, CompRule, RuleBasis, RuleType, RuleUnit } from '../../types/commission';
import { formatRuleBound, formatRuleValue } from '../../utils/commission';

export type EditableField = 'min' | 'max' | 'value';

const FIELDS: EditableField[] = ['min', 'max', 'value'];
const fieldLabel: Record<EditableField, string> = { min: 'Min', max: 'Max', value: 'Value' };

const keyPrefix: Record<RuleType, string> = {
  'Base BPS': 'BT',
  'Monthly Bonus': 'MB',
  Divisor: 'DIV',
  'TL Rate': 'TL',
  'Fee Short': 'FS',
};

const unitFor: Record<RuleType, RuleUnit> = {
  'Base BPS': 'bps',
  'Monthly Bonus': 'bps',
  Divisor: '%',
  'TL Rate': 'bps',
  'Fee Short': '$',
};

const basisFor: Record<RuleType, RuleBasis> = {
  'Base BPS': 'avgUnits',
  'Monthly Bonus': 'units',
  Divisor: 'none',
  'TL Rate': 'volume',
  'Fee Short': 'none',
};

export function cellKey(ruleId: string, field: EditableField): string {
  return `${ruleId}:${field}`;
}

function formatField(rule: CompRule, field: EditableField): string {
  return field === 'value' ? formatRuleValue(rule) : formatRuleBound(rule, field);
}

export function useRulesTable(initialRules: CompRule[], versionNumber: number, editor: string) {
  const [savedRules, setSavedRules] = useState<CompRule[]>(initialRules);
  const [rules, setRules] = useState<CompRule[]>(initialRules);
  const [collapsed, setCollapsed] = useState<Set<RuleType>>(() => new Set());
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const editedCells = useMemo(() => {
    const cells = new Set<string>();
    rules.forEach((rule) => {
      const original = savedRules.find((item) => item.id === rule.id);
      FIELDS.forEach((field) => {
        if (!original || original[field] !== rule[field]) cells.add(cellKey(rule.id, field));
      });
    });
    return cells;
  }, [rules, savedRules]);

  const dirtyRules = useMemo(
    () =>
      rules.filter((rule) => {
        const original = savedRules.find((item) => item.id === rule.id);
        return !original || FIELDS.some((field) => original[field] !== rule[field]);
      }),
    [rules, savedRules],
  );

  function toggleGroup(type: RuleType) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function commitCell(ruleId: string, field: EditableField, raw: string) {
    setEditingCell(null);
    const trimmed = raw.trim().replace(/[$,+%\s]/g, '');
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && Number.isNaN(parsed)) {
      setError('Min, Max and Value accept numbers only.');
      return;
    }
    setError(null);
    setRules((prev) => prev.map((rule) => (rule.id !== ruleId ? rule : { ...rule, [field]: parsed })));
  }

  function addRule(type: RuleType) {
    const count = rules.filter((rule) => rule.ruleType === type).length + 1;
    const id = `new-${keyPrefix[type]}-${Date.now()}`;
    const rule: CompRule = {
      id,
      ruleKey: `V${versionNumber}-${keyPrefix[type]}-${count}`,
      ruleType: type,
      scope: 'Default',
      description: 'New rule',
      basis: basisFor[type],
      min: null,
      max: null,
      value: 0,
      unit: unitFor[type],
      lastModifiedBy: '',
      lastModifiedOn: '',
    };
    setRules((prev) => [...prev, rule]);
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(type);
      return next;
    });
    setEditingCell(cellKey(id, 'value'));
  }

  /** Saves pending edits and returns one audit entry per changed field. */
  function save(reason: string): AuditEntry[] {
    const stamp = Date.now();
    const entries: AuditEntry[] = [];
    dirtyRules.forEach((rule) => {
      const original = savedRules.find((item) => item.id === rule.id);
      FIELDS.forEach((field) => {
        if (original && original[field] === rule[field]) return;
        if (!original && field !== 'value' && rule[field] === null) return;
        entries.push({
          id: `audit-${stamp}-${rule.id}-${field}`,
          when: 'Just now',
          who: editor,
          ruleKey: rule.ruleKey,
          field: original ? fieldLabel[field] : `${fieldLabel[field]} (new rule)`,
          from: original ? formatField(original, field) : '—',
          to: formatField(rule, field),
          reason,
        });
      });
    });
    const dirtyIds = new Set(dirtyRules.map((rule) => rule.id));
    const updated = rules.map((rule) => (dirtyIds.has(rule.id) ? { ...rule, lastModifiedBy: editor, lastModifiedOn: 'Just now' } : rule));
    setRules(updated);
    setSavedRules(updated);
    return entries;
  }

  function cancel() {
    setRules(savedRules);
    setEditingCell(null);
    setError(null);
  }

  return {
    rules,
    collapsed,
    editingCell,
    editedCells,
    dirtyCount: dirtyRules.length,
    error,
    setEditingCell,
    toggleGroup,
    commitCell,
    addRule,
    save,
    cancel,
  };
}
